import { Pie } from 'react-chartjs-2';
import { useState, useEffect } from 'react';
import { Row, Col, Button, Form } from 'react-bootstrap';
import { getUserDemographics } from '../../../services/reportService';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend);

const UserDemographicsReport = () => {
    const [loading, setLoading] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [userDemographicData, setUserDemographicData] = useState(null);
    const [graphData, setGraphData] = useState(null);
    const [selectedFilter, setSelectedFilter] = useState('');

    const handleCheckboxChange = (value) => {
        setSelectedFilter(prev => (prev === value ? '' : value)); // Toggle logic
    };

    const appUserStats = async () => {
        if (!selectedFilter) {
            alert('Please select either Age or Gender');
            return;
        }

        setHasLoaded(true);
        setLoading(true);
        try {
            const userData = await getUserDemographics(selectedFilter);
            setUserDemographicData(userData);
        } catch (err) {
            console.error('Error fetching user demographic report:', err);
            setUserDemographicData(null);
            setGraphData(null);
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        if (!userDemographicData || userDemographicData.length === 0) return;

        // Step 1: Choose the key based on selected filter
        const key = selectedFilter === 'Gender' ? 'gender' : 'ageCategory';

        // Step 2: Count occurrences of each unique value
        const counts = userDemographicData.reduce((acc, item) => {
            const value = item[key];
            if (value) {
                acc[value] = (acc[value] || 0) + 1;
            }
            return acc;
        }, {});

        // Step 3: Extract labels and data
        const labels = Object.keys(counts);
        const dataValues = Object.values(counts);

        // Generate random pastel colours for each label
        const generateColours = (count) => {
            const colours = [];
            for (let i = 0; i < count; i++) {
                const hue = Math.floor(Math.random() * 360);
                colours.push(`hsl(${hue}, 70%, 70%)`);
            }
            return colours;
        };

        const backgroundColours = generateColours(labels.length);

        // Step 4: Set the graph data
        const data = {
            labels: labels,
            datasets: [
                {
                    label: `Users by ${selectedFilter}`,
                    data: dataValues,
                    backgroundColor: backgroundColours,
                    borderColor: backgroundColours.map(c => c.replace('70%', '50%')),
                    borderWidth: 1,
                },
            ],
        };

        setGraphData(data);
        setLoading(false);
    }, [userDemographicData, selectedFilter]);

    useEffect(() => {
        setGraphData(null);
        setUserDemographicData(null);
        setHasLoaded(false);
    }, [selectedFilter]);


    const options = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top',
            },
            title: {
                display: true,
                text: 'User Distribution',
            },
        },
    };

    return (
        <div className="userDemographics component">
            <Row>
                <Col xs={12}>
                    <h3 className="text-center">User Demographics</h3>
                </Col>
            </Row>

            <Row className="mb-3">
                <Col xs={12}>
                    <Form>
                        <Form.Check
                            type="checkbox"
                            id="filter-age"
                            label="Age"
                            checked={selectedFilter === 'Age'}
                            onChange={() => handleCheckboxChange('Age')}
                        />
                        <Form.Check
                            type="checkbox"
                            id="filter-gender"
                            label="Gender"
                            checked={selectedFilter === 'Gender'}
                            onChange={() => handleCheckboxChange('Gender')}
                        />
                    </Form>
                </Col>

                <Col xs={12} className="my-3 text-center">
                    <Button variant="primary" onClick={appUserStats} disabled={loading}>
                        Generate Report
                    </Button>
                </Col>
            </Row>

            <Row>
                <Col xs={12}>
                    {!hasLoaded ? null : loading ? (
                        <p className="text-center">Loading report...</p>
                    ) : (
                        graphData && (
                            <>
                                <Row>
                                    <Col xs={12} className="d-flex justify-content-center">
                                        <Pie data={graphData} options={options} key={selectedFilter} />
                                    </Col>
                                </Row>
                            </>
                        )
                    )}
                </Col>
            </Row>
        </div>
    );
};

export default UserDemographicsReport;
